import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    Button,
    TextField,
    Divider,
    withStyles,
    WithStyles as IWithStyles,
    Theme as ITheme,
} from '@material-ui/core';
import { CloseIcon } from 'src/Assets/svgs/CloseIcon';
import { Grid } from '@mui/material';
import { ReportProblem } from 'src/Features/ProblemReporting/Components/Main';

const styles = (theme: ITheme) => ({
    dialog: {
        maxWidth: ' 100%',
    },
    dialogBox: {
        backgroundColor: '#121217',
        //backgroundColor: 'red',
        zIndex: 999999,
        margin: 0,
        padding: 0,
        border: '1px solid #3F3F50',
        borderRadius: '8px',
    },
    dialogHeader: {
        padding: '12px 16px',
        borderBottom: '1px solid #3F3F50',
        '& .close-icon': {
            cursor: 'pointer',
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            '&:hover': {
                backgroundColor: 'rgba(250, 250, 250, 0.1)',
            },
        },
    },
    dialogTitle: {
        // padding: `${theme.spacing(1.25)}px ${theme.spacing(0.5)}px`,
        '&&': {
            // doubles the class selector to increase specificity
            padding: 0,
            '& h2': {
                fontSize: 16,
            },
        },
    },
    buttonRow: {
        borderTop: '1px solid #3F3F50',
        padding: '16px',
        textAlign: 'right' as 'right',
        gap: 8,
        display: 'flex',
        justifyContent: 'flex-end',
        '& button': {
            borderRadius: 8,
            padding: '0 16px',
            height: 40,
            fontWeight: 400,
            textTransform: 'capitalize',
            '&.MuiButton-contained:hover': {
                backgroundColor: '#B3211E',
            },
        },
    },
    button: {
        marginLeft: theme.spacing(1),
    },
    form: {
        margin: `${theme.spacing(2)} 0px`,
        display: 'flex',
        flexDirection: 'column' as 'column',
    },
    videoTitle: {
        width: '100%',
        marginBottom: 8,
        '& .MuiOutlinedInput-root': {
            borderWidth: 1,
            borderRadius: 8,
            fontSize: 14,
            '& input': {
                padding: '16px 8px',
            },
            '&.MuiOutlinedInput-multiline': {
                padding: 0,
                '& textarea': {
                    padding: '16px 8px',
                },
            },
            '&:hover fieldset': {
                borderWidth: 1,
                borderColor: '#ffffff',
            },
            '& fieldset': {
                borderWidth: 1,
                transition: 'border-width 0.3s ease',
            },
            '&.Mui-focused fieldset': {
                borderWidth: 1,
                borderColor: '#ffffff',
            },
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    videoDescription: {
        width: '100%',
        marginBottom: theme.spacing(1.25),
    },
    description: {
        fontSize: 14,
        fontWeight: 500,
        margin: 0,
    },
});

interface IField {
    label: string;
    name: string;
    required: boolean;
    defaultValue: string;
    placeHolder: string;
    type?: string;
}

interface IProps {
    open: boolean;
    title: string;
    confirmButtonText?: string;
    width?: string;
    fields?: IField[];
    onClose: () => void;
    onConfirm: (form?: object) => void;
    icon?: string | undefined;
    description?: string | undefined;
    contactForm?: boolean;
    showDefaultButton?: boolean;
}

const modalDialog = ({
    classes,
    open,
    title,
    confirmButtonText,
    width,
    fields,
    onClose,
    onConfirm,
    icon,
    description,
    contactForm = false,
    showDefaultButton = true,
}: IProps & IWithStyles) => {
    const [formState, setFormState] = useState({});

    const onUpdate = (field: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) =>
        setFormState({
            ...formState,
            [field]: event.currentTarget.value,
        });

    const isReady = () =>
        fields
            ? Object.entries(
                  formState
              ).every(([key, value]: [string, string]) =>
                  fields.find((field: IField) => field.name === key)?.required
                      ? value.length > 1
                      : true
              )
            : true;

    const form = fields ? (
        <Box className={classes.form}>
            {fields.map(
                (
                    {
                        label,
                        name,
                        required,
                        defaultValue,
                        placeHolder,
                        type = 'text',
                    }: IField,
                    index: number
                ) => {
                    return (
                        <>
                            <label
                                htmlFor={name}
                                style={{ display: 'block', marginBottom: 8 }}
                            >
                                {label}
                                <span style={{ color: '#DB2C29' }}>
                                    {required && ' \u002A'}
                                </span>
                            </label>
                            <TextField
                                key={index}
                                className={classes.videoTitle}
                                required={required}
                                // label={label}
                                id={name}
                                variant='outlined'
                                value={formState[name]}
                                onChange={onUpdate(name)}
                                placeholder={placeHolder}
                                multiline={type === 'textarea'}
                                minRows={type === 'textarea' ? 7 : 1}
                                InputLabelProps={{ shrink: false }}
                            />
                        </>
                    );
                }
            )}
        </Box>
    ) : null;

    useEffect(() => {
        if (fields) {
            const newFormState = {};
            fields.forEach(
                ({ name, defaultValue }: IField) =>
                    (newFormState[name] = defaultValue)
            );
            setFormState(newFormState);
        }
    }, [fields]);

    return (
        <Dialog open={open} onClose={onClose} className={classes.dialog}>
            <Box
                className={classes.dialogBox}
                style={{ width: width || '500px', maxWidth: '100%' }}
            >
                <Grid
                    container
                    spacing={0}
                    alignItems='center'
                    className={classes.dialogHeader}
                >
                    <Grid xs={9}>
                        <Box display='flex' alignItems='center'>
                            {icon && (
                                <img
                                    src={icon}
                                    alt='edit video'
                                    width='16'
                                    height='16'
                                    style={{ marginRight: 12 }}
                                />
                            )}
                            <DialogTitle className={classes.dialogTitle}>
                                {title}
                            </DialogTitle>
                        </Box>
                    </Grid>
                    <Grid
                        xs={3}
                        sx={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <span onClick={onClose} className={'close-icon'}>
                            <CloseIcon />
                        </span>
                    </Grid>
                </Grid>
                <Grid sx={{ padding: '16px' }}>
                    {form && form}
                    {description && (
                        <p className={classes.description}>{description}</p>
                    )}
                    {contactForm && <ReportProblem onClose={onClose} />}
                </Grid>
                {showDefaultButton && (
                    <Box className={classes.buttonRow}>
                        <Button
                            variant='outlined'
                            className={classes.button}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            disabled={!isReady()}
                            onClick={() => onConfirm(formState)}
                        >
                            {confirmButtonText || 'Save'}
                        </Button>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export const ModalDialog = withStyles(styles)(modalDialog);
